import React, { useState, useEffect } from "react";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import Chip from "@mui/joy/Chip";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { gql, useQuery } from "@apollo/client";
import { ColorPaletteProp } from "@mui/joy/styles";
import Pagination from "../components/Pagination";
import { usePagination } from "../components/usePagination";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

const ASSET_REPORTS_QUERY = gql`
  query AssetReports($first: Int, $after: String, $last: Int, $before: String) {
    assetReports(first: $first, after: $after, last: $last, before: $before) {
      edges {
        node {
          createdAt
          id
          reason
          action
          reporter {
            firstName
            lastName
            email
          }
          asset {
            field01
            field02
            field03
            issueNumber
            controlNumber
            plateNumber
            regNumber
            type
            make
            model
            description
            identificationNumber
            engineNumber
            expiryDate
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const Row = (props: { row: any; key?: any }) => {
  const { row } = props;
  const [open, setOpen] = useState(false);

  const formatReason = (reason: string) => {
    return reason.split("_").join(" ");
  };

  const getReasonColor = (reason: string): ColorPaletteProp => {
    const reasonMap: { [key: string]: ColorPaletteProp } = {
      LICENSE_EXPIRED: "danger",
      LICENSE_EXPIRING_SOON: "warning",
      PLATE_DETAILS_INCORRECT: "danger",
      SOMETHING_VISABLY_WRONG: "danger",
      ASSET_WAS_USED_INCORRECTLY: "danger",
    };
    return reasonMap[reason] || "primary";
  };

  return (
    <React.Fragment>
      <tr>
        <td>
          <IconButton
            aria-label="expand row"
            variant="plain"
            color="neutral"
            size="sm"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Avatar
              size="sm"
              alt={`${row.reporter.firstName} ${row.reporter.lastName}`}
            />
            <div>
              <Typography level="body-xs">
                {row.reporter.firstName} {row.reporter.lastName}
              </Typography>
              <Typography
                level="body-xs"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                }}
              >
                {row.reporter.email}
              </Typography>
            </div>
          </Box>
        </td>
        <td>
          <Chip
            variant="soft"
            size="sm"
            color={getReasonColor(row.reason)}
            sx={{ mr: 0.5 }}
          >
            {formatReason(row.reason)}
          </Chip>
        </td>
        <td>
          <Typography level="body-xs">{formatReason(row.action)}</Typography>
        </td>
        <td>
          <Typography level="body-xs">
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(row.createdAt))}
          </Typography>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5}>
            <Table size="sm">
              <tbody>
                <tr>
                  <td>
                    <Typography level="body-xs">Field 01</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.field01}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Field 02</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.field02}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Field 03</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.field03}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Issue Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.issueNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Control Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.controlNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Plate Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.plateNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Reg Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.regNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Asset Type</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.type}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Make</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.make}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Model</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">{row.asset.model}</Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Description</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.description}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">
                      Identification Number
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.identificationNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Engine Number</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {row.asset.engineNumber}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography level="body-xs">Expiry Date</Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {new Intl.DateTimeFormat("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      }).format(new Date(row.asset.expiryDate))}
                    </Typography>
                  </td>
                </tr>
              </tbody>
            </Table>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const AssetReportsTable = () => {
  const [pageSize] = useState(10);
  const { data, loading, error, fetchMore, refetch } = useQuery(
    ASSET_REPORTS_QUERY,
    {
      variables: {
        first: pageSize,
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  const {
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext,
    canGoPrevious,
  } = usePagination({
    pageSize,
    data,
    fetchMore,
    getPageInfo: (data) => data.assetReports.pageInfo,
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) return prev;
      return {
        assetReports: {
          ...fetchMoreResult.assetReports,
          edges: [...fetchMoreResult.assetReports.edges],
        },
      };
    },
  });

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (wsData.type === "asset_report_updated") {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading data: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
              <th style={{ width: 240, padding: "12px 6px" }}>Reporter</th>
              <th style={{ width: 140, padding: "12px 6px" }}>Reason</th>
              <th style={{ width: 140, padding: "12px 6px" }}>Action</th>
              <th style={{ width: 140, padding: "12px 6px" }}>Submitted At</th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoadingMore ? (
              <tr>
                <td colSpan={5}>
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size="sm" />
                  </Box>
                </td>
              </tr>
            ) : (
              data?.assetReports?.edges?.map(({ node: row }) => (
                <Row key={row.id} row={row} />
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      <Pagination
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onPageChange={handlePageChange}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
      />
    </React.Fragment>
  );
};

const AssetReports = () => {
  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Asset Reports
        </Typography>
      </Box>
      <AssetReportsTable />
    </>
  );
};

export default AssetReports;
